import { SetStateAction } from 'react';

import { AnyAction, Dispatch } from 'redux';

import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { APP_GENERAL_ERROR_MSG, appPages } from 'common/constants/TextContent';
import { TOAST_MESSAGE_STATUS } from 'common/constants/ToastMessageConstants';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setAllEntityResponseData,
	setAllOriginalEntityResponseData,
	setEntityCountData,
	setIsPRCEditDataDeleting,
} from 'common/store/reducer/taxonomyReducer';
import {
	arraysAreEqual,
	convertStringToArray,
	normalizeWhitespace,
	toSentenceCase,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import { IListOfMetaData } from '../api/type-def';

import {
	BUSINESS_UNIT_LABEL,
	CONTROL_HEADER_LABEL,
	CONTROL_PARENT_RELATION_DATA,
	CONTROL_TAB_LABEL,
	DATABASE_CATEGORY,
	ENTITY_TYPE,
	FINANCIAL_CRIMES,
	FINANCIAL_SERVICES,
	GENERATE_MODE,
	INDUSTRY_LABEL,
	META_DATA_KEYWORD,
	OBLIGATION_PARENT_RELATION_DATA,
	PRC_META_DATA_KEY,
	PROCESS_PARENT_RELATION_DATA,
	RELATION_MAPPING_TAB_VALUES,
	RISK_PARENT_RELATION_DATA,
	STEPPER_STATUS,
} from '../../../common/constants/TaxonomyConstants';

import {
	deleteAIGeneratedCards,
	getControls,
	getObligations,
	getPolicies,
	getProcesses,
	getRisks,
} from './api';
import {
	IControlHeaderData,
	IControlTestScriptData,
	IEntityDataCount,
	IRenegerateChanges,
	ISelectOption,
} from './api/type-def';

import styles from './Taxonomy.module.scss';

const {
	citationHeaderColor,
	obligationsHeaderColor,
	processesHeaderColor,
	controlsHeaderColor,
	policiesHeaderColor,
	risksHeaderColor,
} = styles;

const {
	CITATION,
	PROCESS,
	CONTROL,
	POLICY,
	RISK,
	REGULATION,
	OBLIGATION,
	GROUP,
	CITATION_GROUP_BU,
	AI_GENERATED_ENTITY,
} = ENTITY_TYPE;

const { AI } = GENERATE_MODE;

const { PWC_CURATED_LIBRARY } = DATABASE_CATEGORY;
const {
	LEGAL_REVIEW_IN_PROGRESS,
	LEGAL_REVIEW_COMPLETED,
	COMPLIANCE_REVIEW_IN_PROGRESS,
	COMPLIANCE_REVIEW_COMPLETED,
	REMEDIATION_TASK_IMPLEMENTATION_IN_PROGRESS,
	TASK_IMPLEMENTATION_COMPLETED,
} = STEPPER_STATUS;

const {
	PROCESSES,
	RISKS,
	CONTROLS,
	OBLIGATIONS,
	POLICIES,
	NONE,
	ALL,
	AI_GENERATED,
} = RELATION_MAPPING_TAB_VALUES;

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_MESSAGE_STATUS;
const { CONTROL_STATEMENT, CONTROL_TEST_SCRIPTS } = CONTROL_TAB_LABEL;
const { FREQUENCY, KEY_CONTROL, TYPE, INDUSTRY_TYPE, AUTOMATIC_MANUAL } =
	CONTROL_HEADER_LABEL;
const { INDUSTRY, BUSINESS_UNIT } = PRC_META_DATA_KEY;
const {
	INDUSTRY_KEYWORD,
	FREQUENCY_KEYWORD,
	TYPE_KEYWORD,
	KEY_CONTROL_KEYWORD,
	AUTOMATED_OR_MANUAL_KEYWORD,
	BUSINESS_UNIT_KEYWORD,
	NEW_KEYWORD,
	TEST_SCRIPTS_KEYWORD,
	STATEMENT_KEYWORD,
} = META_DATA_KEYWORD;
const { parent, prcInventory } = rrcpDashboardRoutes;
const { HOME_PAGE } = appPages;

export const entityDetailsTemplate = [
	{
		label: 'Regulator',
		value: '-',
	},
	{
		label: 'Jurisdiction',
		value: '-',
	},
	{
		label: 'Last Revision',
		value: '-',
	},
	{
		label: 'Status',
		value: '-',
	},
	{
		label: 'Priority',
		value: '-',
	},
	{
		label: 'Deadline',
		value: '-',
	},
];

export const entityDetailsMappingObj: any = {
	Regulator: 'agency',
	Jurisdiction: 'jurisdiction',
	'Last Revision': 'modifiedDate',
	Status: 'status',
	Priority: 'priority',
	Deadline: 'deadline',
};

export const colorMapping = [
	{ type: 'Processes', color: processesHeaderColor },
	{ type: 'Risks', color: risksHeaderColor },
	{ type: 'Controls', color: controlsHeaderColor },
	{ type: 'Policies', color: policiesHeaderColor },
	{ type: 'Obligations', color: obligationsHeaderColor },
	{ type: 'Citations', color: citationHeaderColor },
];

export const citationHeader = ['Processes', 'Risks', 'Controls', 'Policies'];
export const processHeader = ['Risks', 'Controls', 'Obligations', 'Policies'];
export const controlHeader = ['Processes', 'Risks', 'Obligations', 'Policies'];
export const riskHeader = ['Processes', 'Controls', 'Obligations', 'Policies'];
export const policyHeader = ['Processes', 'Risks', 'Controls', 'Obligations'];
export const obligationsHeader = ['Processes', 'Risks', 'Controls', 'Policies'];
export const regulationHeader = [
	'Citations',
	'Processes',
	'Risks',
	'Controls',
	'Policies',
];

export const getTypeForColor = (type: string) => {
	switch (type) {
		case 'Citations':
			return CITATION;
		case 'Processes':
			return PROCESS;
		case 'Controls':
			return CONTROL;
		case 'Policies':
			return POLICY;
		case 'Risks':
			return RISK;
		default:
			return REGULATION;
	}
};

export const getEntityMappingHeaders = (entity: string) => {
	switch (entity?.toLowerCase()) {
		case CITATION.toLowerCase():
			return citationHeader;
		case PROCESS.toLowerCase():
			return processHeader;
		case RISK.toLowerCase():
			return riskHeader;
		case CONTROL.toLowerCase():
			return controlHeader;
		case OBLIGATION.toLowerCase():
			return obligationsHeader;
		case POLICY.toLowerCase():
			return policyHeader;
		case REGULATION.toLowerCase():
			return regulationHeader;
		default:
			return processHeader;
	}
};

const createCardPayload = (type: string, relationData: any) => {
	let finalResponseData: any = [];
	if (relationData && relationData.length > 0) {
		finalResponseData = relationData
			.filter((ele: any) => ele.childType === type)
			.map((el: any) => ({
				owner: '',
				impacted: el.reviewStatus,
				title: el.childName,
				childDisplayId: el.childDisplayId,
				paragraphId: '',
				id: el.childId,
				entityId: el.childId,
				swap: el.swap,
				createdBy: el.createdBy,
				citationGroupId: el?.citationGroupId || null,
			}));
	}
	return finalResponseData;
};

const fetchCitationMappingData = (relationData: any) => [
	{
		type: PROCESS,
		listData: createCardPayload(PROCESS, relationData),
	},
	{
		type: RISK,
		listData: createCardPayload(RISK, relationData),
	},
	{
		type: CONTROL,
		listData: createCardPayload(CONTROL, relationData),
	},
	{
		type: POLICY,
		listData: createCardPayload(POLICY, relationData),
	},
];
const fetchRegulationMappingData = (relationData: any) => [
	{
		type: CITATION,
		listData: createCardPayload(CITATION, relationData),
	},
	{
		type: PROCESS,
		listData: createCardPayload(PROCESS, relationData),
	},
	{
		type: RISK,
		listData: createCardPayload(RISK, relationData),
	},
	{
		type: CONTROL,
		listData: createCardPayload(CONTROL, relationData),
	},
	{
		type: POLICY,
		listData: createCardPayload(POLICY, relationData),
	},
];

const fetchProcessesMappingData = (relationData: any) => [
	{
		type: RISK,
		listData: createCardPayload(RISK, relationData),
	},
	{
		type: CONTROL,
		listData: createCardPayload(CONTROL, relationData),
	},
	{
		type: GROUP,
		listData: createCardPayload(CITATION_GROUP_BU, relationData),
	},
	{
		type: POLICY,
		listData: createCardPayload(POLICY, relationData),
	},
];

const fetchControlMappingData = (relationData: any) => [
	{
		type: PROCESS,
		listData: createCardPayload(PROCESS, relationData),
	},
	{
		type: RISK,
		listData: createCardPayload(RISK, relationData),
	},
	{
		type: GROUP,
		listData: createCardPayload(CITATION_GROUP_BU, relationData),
	},
	{
		type: POLICY,
		listData: createCardPayload(POLICY, relationData),
	},
];

const fetchPoliciesMappingData = (relationData: any) => [
	{
		type: PROCESS,
		listData: createCardPayload(PROCESS, relationData),
	},
	{
		type: RISK,
		listData: createCardPayload(RISK, relationData),
	},
	{
		type: CONTROL,
		listData: createCardPayload(CONTROL, relationData),
	},
	{
		type: GROUP,
		listData: createCardPayload(CITATION_GROUP_BU, relationData),
	},
];

const fetchRisksMappingData = (relationData: any) => [
	{
		type: PROCESS,
		listData: createCardPayload(PROCESS, relationData),
	},
	{
		type: CONTROL,
		listData: createCardPayload(CONTROL, relationData),
	},
	{
		type: GROUP,
		listData: createCardPayload(CITATION_GROUP_BU, relationData),
	},
	{
		type: POLICY,
		listData: createCardPayload(POLICY, relationData),
	},
];

export const fetchRelationMappingData = (
	entityType: string,
	relationData: any,
) => {
	switch (entityType?.toLowerCase()) {
		case REGULATION.toLowerCase():
			return fetchRegulationMappingData(relationData);
		case CITATION.toLowerCase():
			return fetchCitationMappingData(relationData);
		case PROCESS.toLowerCase():
			return fetchProcessesMappingData(relationData);
		case CONTROL.toLowerCase():
			return fetchControlMappingData(relationData);
		case POLICY.toLowerCase():
			return fetchPoliciesMappingData(relationData);
		case RISK.toLowerCase():
			return fetchRisksMappingData(relationData);
		default:
			return fetchCitationMappingData(relationData);
	}
};

export const getStepperIndex = (status: string) => {
	let activeIndex;
	switch (status) {
		case LEGAL_REVIEW_IN_PROGRESS:
			activeIndex = 0;
			break;
		case LEGAL_REVIEW_COMPLETED:
			activeIndex = 1;
			break;
		case COMPLIANCE_REVIEW_IN_PROGRESS:
			activeIndex = 1;
			break;
		case COMPLIANCE_REVIEW_COMPLETED:
			activeIndex = 2;
			break;
		case REMEDIATION_TASK_IMPLEMENTATION_IN_PROGRESS:
			activeIndex = 2;
			break;
		case TASK_IMPLEMENTATION_COMPLETED:
			activeIndex = 3;
			break;
		default:
			activeIndex = 1;
	}
	return activeIndex;
};

export const handleSpecialCharacters = (searchText: any) => {
	const specialCharacters = [
		'+',
		'-',
		'=',
		'&&',
		'/',
		'||',
		'>',
		'<',
		'!',
		'(',
		')',
		'{',
		'}',
		'[',
		']',
		'^',
		'"',
		'~',
		'*',
		'?',
		':',
	];
	const searchTextOriginal = searchText;
	let updatedSearchString = '';
	if (searchTextOriginal.length > 0) {
		searchTextOriginal.split('').forEach((ele: string) => {
			if (specialCharacters.indexOf(ele) === -1) {
				updatedSearchString += ele;
			} else {
				updatedSearchString += `\\${ele}`;
			}
		});
	} else {
		updatedSearchString = searchText;
	}

	return updatedSearchString;
};

export function removeDuplicatesAndChecked(
	object1: ISelectOption[],
	object2: any[],
): ISelectOption[] {
	return object1.map((obj1) => {
		if (object2.includes(obj1.value)) {
			// If the value is found in object2, mark it as checked
			return { ...obj1, checked: true };
		}
		if (obj1.subOptions && obj1.subOptions.length > 0) {
			// If the object has suboptions, recursively check them
			return {
				...obj1,
				subOptions: removeDuplicatesAndChecked(obj1.subOptions, object2),
			};
		}
		// If not found and no suboptions, return the original object
		return obj1;
	});
}

export function updateOptions(obj: ISelectOption[]): ISelectOption[] {
	return obj.map((item) => {
		if (item.subOptions && item.subOptions.length > 0) {
			const checkedSuboptions = item.subOptions.filter(
				(subItem) => subItem.checked,
			);
			const allSuboptionsChecked =
				checkedSuboptions.length === item.subOptions.length;
			return {
				...item,
				indeterminate:
					checkedSuboptions.length > 0 ? !allSuboptionsChecked : false,
				checked: allSuboptionsChecked,
			};
		}
		return item;
	});
}

/**
 * @description method to fetch the sequence of entity based on parent type
 * @param type
 */
export const getChildEntityOptionsData = (type: string) => {
	const dataMap = {
		[PROCESS.toLowerCase()]: PROCESS_PARENT_RELATION_DATA,
		[RISK.toLowerCase()]: RISK_PARENT_RELATION_DATA,
		[CONTROL.toLowerCase()]: CONTROL_PARENT_RELATION_DATA,
		[OBLIGATION.toLowerCase()]: OBLIGATION_PARENT_RELATION_DATA,
	};

	return dataMap[type?.toLowerCase()] || PROCESS_PARENT_RELATION_DATA;
};

/**
 * @description method to extract responseBody from response
 */
export const extractResponseData = async (
	apiMethod: any,
	mappingFilter:
		| {
				parentId: any;
				source?: string;
		  }
		| {
				parentId: any;
				searchText: string;
		  },
) => {
	const response = await apiMethod(mappingFilter);
	return response?.responseBody;
};

/**
 * @description method to fetch combined data sequence based on parent entity type
 * @returns combinedData
 */
export const getCombinedDataBasedOnSequence = (
	parentEntityType: string,
	processData: any,
	riskData: any,
	controlData: any,
	obligationData: any,
	policyData: any,
) => {
	let combinedData: any;

	switch (parentEntityType?.toLowerCase()) {
		case PROCESS.toLowerCase():
			combinedData = [
				...(riskData?.responseBody as any[]),
				...(controlData?.responseBody as any[]),
				...(obligationData?.responseBody as any[]),
				...(policyData?.responseBody as any[]),
			];
			break;
		case RISK.toLowerCase():
			combinedData = [
				...(processData?.responseBody as any[]),
				...(controlData?.responseBody as any[]),
				...(obligationData?.responseBody as any[]),
				...(policyData?.responseBody as any[]),
			];
			break;
		case CONTROL.toLowerCase():
			combinedData = [
				...(processData?.responseBody as any[]),
				...(riskData?.responseBody as any[]),
				...(obligationData?.responseBody as any[]),
				...(policyData?.responseBody as any[]),
			];
			break;
		case OBLIGATION.toLowerCase():
			combinedData = [
				...(processData?.responseBody as any[]),
				...(riskData?.responseBody as any[]),
				...(controlData?.responseBody as any[]),
				...(policyData?.responseBody as any[]),
			];
			break;
		case NONE.toLowerCase():
			combinedData = [
				...(processData?.responseBody as any[]),
				...(riskData?.responseBody as any[]),
				...(controlData?.responseBody as any[]),
				...(obligationData?.responseBody as any[]),
				...(policyData?.responseBody as any[]),
			];
			break;
		default:
			combinedData = [];
	}
	return combinedData;
};

/**
 * @description method to form array of object with respective entity count
 */
export const fetchEntityCountData = (
	parentEntityType: string,
	processCount: number,
	riskCount: number,
	controlCount: number,
	obligationCount: number,
	policyCount: number,
	aiGeneratedCount?: number,
) => {
	const entitySequenceMap: any = {
		Process: [
			{
				entityName: 'ALL',
				entityCount: [
					riskCount,
					controlCount,
					obligationCount,
					policyCount,
				]?.reduce((acc, curr) => acc + curr, 0),
			},
			{
				entityName: 'RISKS',
				entityCount: riskCount,
			},
			{
				entityName: 'CONTROLS',
				entityCount: controlCount,
			},
			{
				entityName: 'OBLIGATIONS',
				entityCount: obligationCount,
			},
			{
				entityName: 'POLICIES',
				entityCount: policyCount,
			},
			{
				entityName: 'AI_GENERATED',
				entityCount: aiGeneratedCount,
			},
		],
		Risk: [
			{
				entityName: 'ALL',
				entityCount: [
					processCount,
					controlCount,
					obligationCount,
					policyCount,
				]?.reduce((acc, curr) => acc + curr, 0),
			},
			{
				entityName: 'PROCESSES',
				entityCount: processCount,
			},
			{
				entityName: 'CONTROLS',
				entityCount: controlCount,
			},
			{
				entityName: 'OBLIGATIONS',
				entityCount: obligationCount,
			},
			{
				entityName: 'POLICIES',
				entityCount: policyCount,
			},
			{
				entityName: 'AI_GENERATED',
				entityCount: aiGeneratedCount,
			},
		],
		Control: [
			{
				entityName: 'ALL',
				entityCount: [
					processCount,
					riskCount,
					obligationCount,
					policyCount,
				]?.reduce((acc, curr) => acc + curr, 0),
			},
			{
				entityName: 'PROCESSES',
				entityCount: processCount,
			},
			{
				entityName: 'RISKS',
				entityCount: riskCount,
			},
			{
				entityName: 'OBLIGATIONS',
				entityCount: obligationCount,
			},
			{
				entityName: 'POLICIES',
				entityCount: policyCount,
			},
			{
				entityName: 'AI_GENERATED',
				entityCount: aiGeneratedCount,
			},
		],
		Obligation: [
			{
				entityName: 'ALL',
				entityCount: [
					processCount,
					riskCount,
					controlCount,
					policyCount,
				]?.reduce((acc, curr) => acc + curr, 0),
			},
			{
				entityName: 'PROCESSES',
				entityCount: processCount,
			},
			{
				entityName: 'RISKS',
				entityCount: riskCount,
			},
			{
				entityName: 'CONTROLS',
				entityCount: controlCount,
			},
			{
				entityName: 'POLICIES',
				entityCount: policyCount,
			},
		],
		None: [
			{
				entityName: 'ALL',
				entityCount: [
					processCount,
					riskCount,
					controlCount,
					obligationCount,
					policyCount,
				]?.reduce((acc, curr) => acc + curr, 0),
			},
			{
				entityName: 'PROCESSES',
				entityCount: processCount,
			},
			{
				entityName: 'RISKS',
				entityCount: riskCount,
			},
			{
				entityName: 'CONTROLS',
				entityCount: controlCount,
			},
			{
				entityName: 'OBLIGATIONS',
				entityCount: obligationCount,
			},
			{
				entityName: 'POLICIES',
				entityCount: policyCount,
			},
		],
	};

	return entitySequenceMap[parentEntityType] || [];
};

/**
 * @description method to fetch all data for all entities
 */
export const fetchAllData = async (
	params: any,
	searchText: string,
	parentId: string,
	parentEntityType: string,
	updateAction: Dispatch<AnyAction>,
): Promise<any[]> => {
	const processPromise = getProcesses(params);
	const riskPromise = getRisks(params);
	const controlPromise = getControls(params);
	const obligationPromise = getObligations({
		...{ searchText },
		...{ parentId },
	});
	const policyPromise = getPolicies(params);

	try {
		const [
			processData,
			riskData,
			controlData,
			obligationData,
			policyData,
		]: any = await Promise.all([
			processPromise,
			riskPromise,
			controlPromise,
			obligationPromise,
			policyPromise,
		]);

		const combinedData = getCombinedDataBasedOnSequence(
			parentEntityType,
			processData,
			riskData,
			controlData,
			obligationData,
			policyData,
		);
		const entityCountObject: IEntityDataCount[] = fetchEntityCountData(
			parentEntityType,
			Number(processData?.responseBody?.length),
			Number(riskData?.responseBody?.length),
			Number(controlData?.responseBody?.length),
			Number(obligationData?.responseBody?.length),
			Number(policyData?.responseBody?.length),
		);
		updateAction(setEntityCountData(entityCountObject));
		return combinedData;
	} catch (error) {
		return [];
	}
};

/**
 * @description method to fetch respective api call for entities
 */
export const fetchAPIBasedOnEntity = async (
	tabValue: string,
	mappingFilter:
		| {
				parentId: any;
				source?: string;
		  }
		| {
				parentId: any;
				searchText: string;
		  },
): Promise<any> => {
	let response;
	switch (tabValue?.toLowerCase()) {
		case PROCESSES.toLowerCase():
			response = extractResponseData(getProcesses, mappingFilter);
			break;
		case RISKS.toLowerCase():
			response = extractResponseData(getRisks, mappingFilter);
			break;
		case CONTROLS.toLowerCase():
			response = extractResponseData(getControls, mappingFilter);
			break;
		case OBLIGATIONS.toLowerCase():
			response = extractResponseData(getObligations, mappingFilter);
			break;
		case POLICIES.toLowerCase():
			response = extractResponseData(getPolicies, mappingFilter);
			break;
		default:
			response = [];
			break;
	}
	return response;
};

/**
 * @description method to handle change in all options
 */
export const handleSelectAllChange = (
	selectAll: boolean,
	entityResponseData: any[],
	updateSelectedOptions: (value: any) => void,
	updateSelectAll: (value: any) => void,
) => {
	if (selectAll) {
		updateSelectedOptions([]);
	} else {
		const filteredSelectedOptions = entityResponseData?.filter(
			(data) => !data?.mapped,
		);
		updateSelectedOptions(filteredSelectedOptions);
	}
	updateSelectAll(!selectAll);
};

/**
 * @description method to handle change in one option
 */
export const handleSingleOptionChange = (
	selectedOptions: any,
	option: any,
	setSelectedOptions: any,
) => {
	const existingOption = selectedOptions?.find(
		(selectedOption: any) => selectedOption?.id === option?.id,
	);
	if (existingOption) {
		setSelectedOptions(
			selectedOptions?.filter(
				(selectedOption: any) => selectedOption?.id !== option?.id,
			),
		);
	} else {
		const filteredSelectedOptions = selectedOptions?.filter(
			(data: any) => !data?.mapped,
		);
		setSelectedOptions([...filteredSelectedOptions, option]);
	}
};

/**
 * @description method to fetch the toast msg data
 */
export const fetchToastMsgData = (
	response: any,
	successStatusCode: number,
	updateAction: Dispatch<any>,
	successToastMsg: string | null,
	failurToastMsg?: string,
) => {
	if (response?.statusCode === successStatusCode) {
		updateAction(
			setToastMessage({
				message: successToastMsg || '',
				severity: TOAST_SUCCESS,
				isToastOpen: true,
			}),
		);
	} else {
		const errorMsg = response?.errorBody?.errorMessage as string;
		updateAction(
			setToastMessage({
				message: failurToastMsg || errorMsg || APP_GENERAL_ERROR_MSG,
				severity: TOAST_ERROR,
				isToastOpen: true,
			}),
		);
	}
};

/**
 * @description method to fetch the color of border based on type entity
 * @param type
 */
export const fetchRelationshipCardBorderStyling = (type: string) => {
	switch (type?.toLowerCase()) {
		case PROCESS?.toLowerCase():
			return 'border-process';
		case RISK?.toLowerCase():
			return 'border-risk';
		case POLICY?.toLowerCase():
			return 'border-policy';
		case CONTROL?.toLowerCase():
			return 'border-control';
		case OBLIGATION?.toLowerCase():
			return 'border-obligation';
		default:
			return '';
	}
};

/**
 * @description method to fetch the filter type based on tabValue
 * @param tabValue
 * @returns {string} Corresponding filter type or an empty string if no match is found
 */
export const fetchFilterTypeBasedOnTabValue = (tabValue: string) => {
	if (!tabValue) return '';

	const tabValueLowerCase = tabValue.toLowerCase();

	const filterTypeMap: { [key: string]: string } = {
		[PROCESSES.toLowerCase()]: PROCESS,
		[RISKS.toLowerCase()]: RISK,
		[CONTROLS.toLowerCase()]: CONTROL,
		[OBLIGATIONS.toLowerCase()]: CITATION_GROUP_BU,
		[POLICIES.toLowerCase()]: POLICY,
		[AI_GENERATED.toLowerCase()]: AI_GENERATED_ENTITY,
	};

	return filterTypeMap[tabValueLowerCase] || '';
};

/*
 * @description method to implement search functionality
 */
export const searchForDataInMapping = (
	originalResponseData: any,
	searchText: string,
	tabValue: string,
) => {
	if (!originalResponseData) return [];

	const normalizedSearchText = normalizeWhitespace(searchText);

	return originalResponseData.filter((obj: any) => {
		const searchField =
			tabValue?.toLowerCase() === OBLIGATIONS.toLowerCase()
				? obj?.groupName
				: obj?.name;
		const matchesSearchField = normalizeWhitespace(
			searchField?.toLowerCase(),
		)?.includes(normalizedSearchText?.toLowerCase());
		const matchesDisplayId = obj?.displayId
			?.toLowerCase()
			?.includes(searchText?.toLowerCase());
		return matchesSearchField || matchesDisplayId;
	});
};

/**
 * @description method to get label based on entityType
 * @param entityType
 * @param tabValue
 * @returns displayText
 */
export const getSummaryLabel = (entityType: string, tabValue: string) => {
	let displayText = 'Summary';
	switch (entityType?.toLowerCase()) {
		case RISK?.toLowerCase():
			displayText = 'Risk statement';
			break;
		case CONTROL?.toLowerCase():
			displayText = toSentenceCase(tabValue);
			break;
		default:
			break;
	}
	return displayText;
};

/**
 * @description method to fetch all tab data
 */
export const fetchAllTabsData = async (
	entityType: string,
	riskRefTaxonomySourceFiltersKey: string,
	setIsAllEntityDataLoading: any,
	updateAction: Dispatch<AnyAction>,
	entityId: string | undefined,
) => {
	setIsAllEntityDataLoading(true);
	const sourceParam = riskRefTaxonomySourceFiltersKey || 'PWC';
	const querySourceParam = sourceParam ? { source: sourceParam } : '';
	const queryMappedId = {
		parentId: entityId,
	};

	const response = await fetchAllData(
		{ ...querySourceParam, ...queryMappedId },
		'',
		entityId || '',
		entityType,
		updateAction,
	);
	setIsAllEntityDataLoading(false);
	updateAction(setAllEntityResponseData(response));
	updateAction(setAllOriginalEntityResponseData(response));
};

/**
 * @description method to fetch data based on tab value
 */
export const fetchCurrentEntityData = (
	tabValue: string,
	allTabEntityData: any,
	specificEntityData: any,
) => {
	const currentEntityData =
		tabValue?.toLowerCase() === ALL.toLowerCase()
			? allTabEntityData
			: specificEntityData;

	return currentEntityData;
};

/**
 * @description method to fetch initial control test data from api
 */
export const fetchInitialControlTestData = (
	responseBody: any,
	hasInvManagerAccess: boolean | undefined,
) => [
	{
		scriptLabel: CONTROL_STATEMENT,
		scriptValue: fetchKeyBasedOnRole(
			hasInvManagerAccess,
			STATEMENT_KEYWORD,
			responseBody,
		),
	},
	{
		scriptLabel: CONTROL_TEST_SCRIPTS,
		scriptValue: fetchKeyBasedOnRole(
			hasInvManagerAccess,
			TEST_SCRIPTS_KEYWORD,
			responseBody,
		),
	},
];

/**
 * @description method to fetch control header data from
 * @param responseBody
 * @param riskRefTaxonomySourceFiltersKey
 * @param hasInvManagerAccess
 */
export const fetchControlHeaderData = (
	responseBody: any,
	riskRefTaxonomySourceFiltersKey: string,
	hasInvManagerAccess: boolean | undefined,
) => [
	{
		headerLabel: FREQUENCY,
		labelValue: fetchKeyBasedOnRole(
			hasInvManagerAccess,
			FREQUENCY_KEYWORD,
			responseBody,
		),
		headerKey: FREQUENCY_KEYWORD,
	},
	{
		headerLabel: KEY_CONTROL,
		labelValue: fetchKeyBasedOnRole(
			hasInvManagerAccess,
			KEY_CONTROL_KEYWORD,
			responseBody,
		),
		headerKey: KEY_CONTROL_KEYWORD,
	},
	{
		headerLabel: TYPE,
		labelValue: fetchKeyBasedOnRole(
			hasInvManagerAccess,
			TYPE_KEYWORD,
			responseBody,
		),
		headerKey: TYPE_KEYWORD,
	},

	{
		headerLabel: AUTOMATIC_MANUAL,
		labelValue: fetchKeyBasedOnRole(
			hasInvManagerAccess,
			AUTOMATED_OR_MANUAL_KEYWORD,
			responseBody,
		),
		headerKey: AUTOMATED_OR_MANUAL_KEYWORD,
	},
	{
		headerLabel:
			riskRefTaxonomySourceFiltersKey?.toLowerCase() ===
			PWC_CURATED_LIBRARY?.toLowerCase()
				? INDUSTRY_TYPE
				: BUSINESS_UNIT_LABEL,
		labelValue: convertStringToArray(
			fetchKeyBasedOnRole(hasInvManagerAccess, INDUSTRY_KEYWORD, responseBody),
		),
		headerKey: INDUSTRY_KEYWORD,
	},
];

/**
 * @description method to fetch the state to disable tab
 * @param index
 * @param statement
 * @param controlTestScripts
 * @returns disabled
 */
export const disableControlTab = (
	index: number,
	statement: string,
	controlTestScripts: IControlTestScriptData[],
) => {
	const disabled =
		(index === 1 && !statement) ||
		(index === 2 &&
			!controlTestScripts?.some(
				(item) => item?.scriptLabel === CONTROL_TEST_SCRIPTS,
			));

	return disabled;
};

/**
 * @description method to fetch the label based on meta data
 * @param label
 */
export const fetchLabelBasedOnMetaData = (label: string) => {
	const updatedLabel = label?.replace(' : ', '').toLowerCase();

	const labelMap: { [key: string]: string } = {
		[INDUSTRY_LABEL?.toLowerCase()]: INDUSTRY,
		[INDUSTRY_TYPE?.toLowerCase()]: INDUSTRY,
		[FREQUENCY?.toLowerCase()]: FREQUENCY,
		[KEY_CONTROL?.toLowerCase()]: KEY_CONTROL,
		[TYPE?.toLowerCase()]: TYPE,
		[AUTOMATIC_MANUAL?.toLowerCase()]: AUTOMATIC_MANUAL,
		[BUSINESS_UNIT_LABEL?.toLowerCase()]: BUSINESS_UNIT,
	};

	return labelMap[updatedLabel] || '';
};

/**
 * @description method to populated the selected options with meta data
 * @param value
 * @param metaData
 */
export const filterMetaData = (
	value: string | string[],
	metaData: IListOfMetaData[],
) => {
	const updatedValue: any = Array?.isArray(value) ? value : [value];
	const updatedMetaData = metaData?.filter((item) =>
		updatedValue?.includes(item?.displayValue),
	);
	return updatedMetaData;
};

/**
 * @description method to fetch the keyword based on label
 * @param label
 */
export const fetchKeywordBasedOnLabel = (label: string) => {
	const updatedLabel = label?.replace(' : ', '').toLowerCase();

	const keywordMap: { [key: string]: string } = {
		[INDUSTRY_LABEL?.toLowerCase()]: INDUSTRY_KEYWORD,
		[INDUSTRY_TYPE?.toLowerCase()]: INDUSTRY_KEYWORD,
		[BUSINESS_UNIT_LABEL?.toLowerCase()]: INDUSTRY_KEYWORD,
		[FREQUENCY?.toLowerCase()]: FREQUENCY_KEYWORD,
		[TYPE?.toLowerCase()]: TYPE_KEYWORD,
		[KEY_CONTROL?.toLowerCase()]: KEY_CONTROL_KEYWORD,
	};

	return keywordMap[updatedLabel] || AUTOMATED_OR_MANUAL_KEYWORD;
};

/**
 * @description method to fetch the if meta data is industry type of business unit
 * @param metaDataKey
 */
export const isIndustryTypeOrBusinessUnit = (metaDataKey: string) => {
	const flag =
		metaDataKey?.toLowerCase() === INDUSTRY_KEYWORD?.toLowerCase() ||
		metaDataKey?.toLowerCase() === BUSINESS_UNIT_KEYWORD?.toLowerCase();
	return flag;
};

const checkForAllValues = (
	initialDetails: any,
	metaDataKey: string,
	value: string | IListOfMetaData[],
) => {
	if (
		initialDetails[`${metaDataKey}${NEW_KEYWORD}`] !== '' &&
		initialDetails[`${metaDataKey}${NEW_KEYWORD}`] !== null
	) {
		return value === initialDetails[`${metaDataKey}${NEW_KEYWORD}`];
	}
	return value === initialDetails[metaDataKey];
};
/**
 * @description method to disable update btn
 * @param isMultiSelect
 * @param value
 * @param initialDetails
 * @param metaDataKey
 */
export const checkStatusOfUpdateBtn = (
	isMultiSelect: boolean,
	value: string | IListOfMetaData[],
	initialDetails: any,
	metaDataKey: string,
) => {
	const arrayData = Array?.isArray(initialDetails[metaDataKey])
		? initialDetails[metaDataKey]
		: [initialDetails[metaDataKey]];
	return isMultiSelect
		? arraysAreEqual(value, arrayData)
		: checkForAllValues(initialDetails, metaDataKey, value);
};

/**
 * @description method to fetch key based on role
 * @param hasInvManagerAccess
 * @param key
 * @param responseBody
 */
export const fetchKeyBasedOnRole = (
	hasInvManagerAccess: boolean | undefined,
	key: string,
	responseBody: any,
) => {
	const renderValueToEditor = responseBody[`${key}${NEW_KEYWORD}`]
		? responseBody[`${key}${NEW_KEYWORD}`]
		: responseBody?.[key];

	const finalMetaDataValue = hasInvManagerAccess
		? renderValueToEditor
		: responseBody?.[key];
	return finalMetaDataValue;
};

/**
 * @description method to fetch status if relation data has AI generated cards
 * @param relationData
 */
export const containsAIGeneratedCards = (relationData: any[]) =>
	relationData?.some(
		(item: any) => item?.generateMode?.toLowerCase() === AI.toLowerCase(),
	);

/**
 * @description method to update control data when entityType is control
 */
export const updateControlHeader = (
	entityType: string,
	metaDataKey: string,
	entityValues: string[],
	setControlHeader: (value: SetStateAction<IControlHeaderData[]>) => void,
) => {
	if (entityType?.toLowerCase() === CONTROL?.toLowerCase()) {
		setControlHeader((prevState: any) => {
			const prevData = [...prevState];
			prevData.forEach((ele: any) => {
				if (ele?.headerKey === metaDataKey) {
					ele.labelValue =
						metaDataKey === INDUSTRY_KEYWORD ? entityValues : entityValues[0];
				}
			});
			return prevData;
		});
	}
};

/**
 * @description method to fetch the toast msg data
 */
export const fetchToastMsg = (
	response: any,
	statusCode: number,
	successMsg: string,
	failureMsg: string,
	updateAction: Dispatch<any>,
) => {
	if (response?.statusCode === statusCode) {
		updateAction(
			setToastMessage({
				message: successMsg,
				severity: TOAST_SUCCESS,
				isToastOpen: true,
			}),
		);
	} else {
		updateAction(
			setToastMessage({
				message: failureMsg,
				severity: TOAST_ERROR,
				isToastOpen: true,
			}),
		);
	}
};

/**
 * @description method to disable the btn if user has edit access and has new values
 */
export const disableBtnBasedOnRoleAndNewValue = (
	hasInvManagerAccess: boolean | undefined,
	key: string,
	initialDetails: any,
	editAccessEnabled: boolean | undefined,
) => {
	if (editAccessEnabled && hasInvManagerAccess) {
		return false;
	}
	return hasInvManagerAccess && initialDetails?.[`${key}${NEW_KEYWORD}`];
};

/**
 * @description method to select meta data from dropdown
 */
export const selectMetaDataFromDropdown = (
	isMultiSelect: boolean,
	listOfMetaData: IListOfMetaData[],
	targetValue: string | IListOfMetaData[],
	initialDetails: any,
	metaDataKey: string,
	setDisableUpdateBtn: any,
	setMultiSelectedMetaDataOption: any,
) => {
	let selectedData: any[] = [];
	if (isMultiSelect) {
		const valueArray: any = targetValue;
		selectedData = valueArray?.map((i: string) =>
			listOfMetaData?.find((item) => item?.value === i),
		);
	} else {
		selectedData = listOfMetaData?.filter(
			(item) => item?.value === targetValue,
		);
	}
	// to disable the update btn when old and new selection is the same
	setDisableUpdateBtn(
		checkStatusOfUpdateBtn(
			isMultiSelect,
			targetValue,
			initialDetails,
			metaDataKey,
		),
	);
	setMultiSelectedMetaDataOption(
		selectedData?.filter((item: any) => item !== undefined),
	);
};

/**
 * @description method to delete the pending AI generated cards
 * @param parentId
 * @param data
 */
export const deletePendingAICards = async (
	parentId: string,
	data: IRenegerateChanges,
	updateAction: Dispatch<AnyAction>,
) => {
	updateAction(setIsPRCEditDataDeleting(true));
	const res = await deleteAIGeneratedCards(parentId, data);
	updateAction(setIsPRCEditDataDeleting(false));
	return res;
};

/**
 * @description method to prepare payload for regenerate changes
 */
export const fetchPayloadForRegenerateChanges = (
	entityId: string,
	entityType: string,
	entityName: string,
	riskRefTaxonomySourceFiltersKey: string,
	industryType: string,
) => {
	const payload = {
		parentObjectId: entityId,
		parentObjectType: entityType,
		text: entityName,
		source: riskRefTaxonomySourceFiltersKey,
		industryType,
	};
	return payload;
};

/**
 * @description method to disable submit changes btn
 */
export const disableSubmitChangesBtn = (
	updatedPayloadData: object,
	isRegenerateBtnClicked: boolean,
	isRegenerating: boolean,
	isDeleting: boolean,
	isDiscardBtnClicked: boolean,
) =>
	!Object?.keys(updatedPayloadData)?.length &&
	(!isRegenerateBtnClicked ||
		!isRegenerating ||
		!(isDeleting && isDiscardBtnClicked));

/**
 * @description method to render item only for PRC screen
 * @param entityType
 */
export const renderOnlyForPRC = (entityType: string) =>
	entityType?.toLowerCase() === RISK.toLowerCase() ||
	PROCESS.toLowerCase() ||
	CONTROL.toLowerCase();

/**
 * @description method to render covered pdt
 * @param industry
 */
export const renderCoveredPdts = (industry: string | string[]) =>
	industry?.includes(FINANCIAL_SERVICES) ||
	industry?.includes(FINANCIAL_CRIMES);

/**
 * @description method to navigate to page
 * @param routePath
 */
export const navigateToPage = (routePath: string) =>
	routePath === HOME_PAGE
		? urlWithTenant(`${parent}`)
		: urlWithTenant(`/ ${prcInventory}`);

/**
 * @description method to fetch value based on incoming type
 * @param value
 */
export const fetchValueBasedOnIncomingType = (value: string | string[]) =>
	Array?.isArray(value) ? value?.join(', ') : value;

/**
 * @description method to fetch params for filters
 * @param page
 * @param filterType
 * @param sort
 */
export const fetchParamsForFilter = (
	page: number,
	filterType: string,
	sort: string,
) => {
	const params = {
		page,
		filterType,
		sort,
	};

	return params;
};

export const updatePayloadToCheckWithAuditKey = (
	payload: any,
	auditKey: string,
	auditID: string,
) => {
	const updatedPayload = { ...payload }; // Create a shallow copy of the payload

	const updateKey = (obj: any) => {
		Object.keys(obj).forEach((key) => {
			if (key === auditKey) {
				obj[key].id = auditID;
			} else if (typeof obj[key] === 'object' && obj[key] !== null) {
				updateKey(obj[key]); // Recursively update nested objects
			}
		});
	};

	updateKey(updatedPayload);
	return updatedPayload;
};
